import React from 'react';
import { Box, Container, Typography, Grid, Card, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import DiscountIcon from '@mui/icons-material/Discount';  
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import NavBar from './shared/NavBar';

const SchoolSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'linear-gradient(135deg, #FFF8F3 0%, #FFF 100%)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    backgroundImage: 'url("/pattern-bg.png")',
    opacity: 0.1,
    pointerEvents: 'none',
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #FFF8F3 0%, #FFF 100%)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,107,53,0.08)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(255,107,53,0.15)',
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(255,107,53,0.1) 0%, rgba(255,107,53,0.05) 100%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& .MuiSvgIcon-root': {
    fontSize: '40px',
    color: '#FF6B35',
  },
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(4),
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF8F3 100%)',
  boxShadow: '0 8px 24px rgba(255,107,53,0.1)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,107,53,0.08)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(255,107,53,0.15)',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#1A1A1A',
  marginBottom: theme.spacing(2),
  fontWeight: 700,
  textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  color: '#666666',
  marginBottom: theme.spacing(6),
  maxWidth: '800px',
  margin: '0 auto',
}));

const ActionButton = styled(Button)(({ theme, color = '#FF6B35' }) => ({
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '30px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  marginTop: theme.spacing(4),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  }
}));

const Schools = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    
  };

  return (
    <Box>
      <NavBar />
      {/* For Schools Section */}
      <SchoolSection>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: '#FF6B35',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                  mb: 3
                }}
              >
                For <span style={{ color: '#06AED5' }}>Schools</span>
              </Typography>
              <Typography 
                variant="h5" 
                paragraph 
                color="textSecondary"
                sx={{ mb: 4 }}
              >
                Empower your students with current affairs knowledge
              </Typography>
              <Typography paragraph>
                Join YoungPresso to keep your students informed and engaged with current affairs.
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  backgroundColor: 'rgba(6, 174, 213, 0.1)',
                  border: '2px solid #06AED5',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#06AED5',
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  For School Partnerships & Queries
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#1A1A1A',
                    fontSize: '1.1rem'
                  }}
                >
                  Contact us at:{' '}
                  <Typography 
                    component="a" 
                    href="mailto:info@youngpresso.com"
                    sx={{ 
                      color: '#FF6B35',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    info@youngpresso.com
                  </Typography>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FeatureCard>
                    <IconWrapper>
                      <DiscountIcon />
                    </IconWrapper>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1A1A1A', fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      Discounted Price
                    </Typography>
                    <Typography color="textSecondary">
                      Special discounts given to school Students through Customized Coupon Code                 </Typography>
                  </FeatureCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FeatureCard>
                    <IconWrapper>
                      <SchoolIcon />
                    </IconWrapper>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1A1A1A', fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      Contests and Rewards
                    </Typography>
                    <Typography color="textSecondary">
                      Contests are managed by YoungPresso and to motivate Students we provide Rewards for students too
                    </Typography>
                  </FeatureCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FeatureCard>
                    <IconWrapper>
                      <AutoAwesomeMotionIcon />
                    </IconWrapper>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1A1A1A', fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      White-labelled Magazine
                    </Typography>
                    <Typography color="textSecondary">
                      We provide option for White-labelled Magazine for your school. We create with your school's name and Logo
                    </Typography>
                  </FeatureCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FeatureCard>
                    <IconWrapper>
                      <ThumbUpOffAltIcon />
                    </IconWrapper>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1A1A1A', fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      Zero-Hassle for School
                    </Typography>
                    <Typography color="textSecondary">
                      We handle all the content creation, communication, support and contests so schools can focus on their core educational mission
                    </Typography>
                  </FeatureCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </SchoolSection>




    </Box>
  );
};

export default Schools;
